// frontend/src/components/OnboardingChecklist.jsx
import React, { useState } from 'react';
import { useLanguage } from '../utils/i18n';
import { useNotification } from '../utils/NotificationContext';
import {
  Sparkles,
  Check,
  X,
  Palette,
  QrCode,
  Zap,
  Printer
} from 'lucide-react';

export default function OnboardingChecklist({
  business,
  userEmail,
  clients = [],
  totalVisits = 0,
  isDemo = false
}) {
  const { t } = useLanguage();
  const { showNotification } = useNotification();

  const dismissedKey = `wafiyo_onboarding_dismissed_${business?.id || 'demo'}`;

  const [isDismissed, setIsDismissed] = useState(() => {
    // If business has lots of clients/visits, consider them existing users
    if (clients.length >= 3 && totalVisits >= 5) return true;
    return localStorage.getItem(dismissedKey) === 'true';
  });

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(dismissedKey, 'true');
    showNotification(t('onboarding_dismiss_hint') || 'You can reopen this guide anytime from Settings.', 'info');
  };

  if (isDemo || isDismissed) {
    return null;
  }

  const steps = [
    {
      num: 1,
      icon: Palette,
      title: t('onboarding_step1_title') || 'Design Your Loyalty Card',
      desc: t('onboarding_step1_desc') || 'Choose your brand colors, logo, and set your reward threshold.'
    },
    {
      num: 2,
      icon: QrCode,
      title: t('onboarding_step2_title') || 'Customers Scan to Join',
      desc: t('onboarding_step2_desc') || 'Clients scan your counter QR with their camera to instantly add the pass to Apple or Google Wallet.'
    },
    {
      num: 3,
      icon: Zap,
      title: t('onboarding_step3_title') || 'Stamp Visits in Seconds',
      desc: t('onboarding_step3_desc') || 'When customers visit, scan their pass QR code with your phone or search them to record stamps.'
    },
    {
      num: 4,
      icon: Printer,
      title: t('onboarding_step4_title') || 'Display Your Counter Stand',
      desc: t('onboarding_step4_desc') || 'Print your ready-to-use QR stand or display it at your cash register for effortless customer signups.'
    }
  ];

  return (
    <section className="dashboard-section-padding" style={{ paddingBottom: '12px' }}>
      <div className="onboarding-card-wrapper">
        {/* Header */}
        <div className="onboarding-header">
          <div className="onboarding-header-left">
            <div className="onboarding-badge-icon">
              <Sparkles size={18} color="var(--primary)" />
            </div>
            <div>
              <h2 className="onboarding-title">
                {t('onboarding_title') || 'Quick Setup Guide'}
              </h2>
              <p className="onboarding-subtitle">
                {t('onboarding_subtitle') || '4 simple steps to get the most out of your loyalty program.'}
              </p>
            </div>
          </div>

          <div className="onboarding-header-actions">
            <button
              className="onboarding-icon-btn"
              onClick={handleDismiss}
              title={t('dismiss') || 'Dismiss'}
              aria-label="Dismiss guide"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Steps List (Simple Text Steps) */}
        <div className="onboarding-steps-list">
          {steps.map((step) => {
            const IconComponent = step.icon;
            return (
              <div key={step.num} className="onboarding-step-item">
                <div className="onboarding-step-top">
                  <div className="onboarding-step-title-group">
                    <div className="onboarding-step-badge">
                      <span>{step.num}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <IconComponent size={15} style={{ color: 'var(--primary)', opacity: 0.85 }} />
                      <h3 className="onboarding-step-heading">{step.title}</h3>
                    </div>
                  </div>
                </div>
                <p className="onboarding-step-description" style={{ marginLeft: '34px' }}>
                  {step.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Action Button: I got it! */}
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn-step-primary"
            onClick={handleDismiss}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '10px',
              cursor: 'pointer'
            }}
          >
            <Check size={16} />
            <span>{t('onboarding_got_it') || '¡Entendido!'}</span>
          </button>
        </div>
      </div>
    </section>
  );
}
